import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Recipe } from '@/types/recipe';
import { RecipeWithStats } from '@/services/recipe/recipeStatsService';
import { MealCompositionWithRecipes } from '@/types/mealComposition';
import { mealCompositionService } from '@/services/mealComposition';
import { MEAL_COMPOSITIONS } from '@/constants/queryKeys';
import { useCurrentUserFamily } from '@/hooks/useCurrentUserFamily';
import { useRecipeStats } from '@/hooks/useRecipeStats';
import RecipeFilters, { SortOption, GroupOption } from '@/components/recipe-collection/RecipeFilters';
import RecipeImageSelectionCard from './RecipeImageSelectionCard';
import CompactRecipeImageSelectionCard from './CompactRecipeImageSelectionCard';
import CompositionImageSelectionCard from './CompositionImageSelectionCard';

type SelectedItem = 
  | { type: 'recipe'; data: Recipe } 
  | { type: 'composition'; data: MealCompositionWithRecipes } 
  | null;

interface ImageSelectionGridProps {
  onSelect: (item: SelectedItem) => void;
  isSubmitting?: boolean;
}

const ImageSelectionGrid: React.FC<ImageSelectionGridProps> = ({
  onSelect,
  isSubmitting = false
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('date-desc');
  const [groupBy, setGroupBy] = useState<GroupOption>('all');
  const [excludeCompositions, setExcludeCompositions] = useState(false);
  const { familyId } = useCurrentUserFamily();

  // Fetch recipes with stats
  const { data: recipes = [], isLoading: recipesLoading } = useRecipeStats();

  // Fetch meal compositions
  const { data: compositions = [], isLoading: compositionsLoading } = useQuery({
    queryKey: [MEAL_COMPOSITIONS],
    queryFn: mealCompositionService.getMealCompositions,
    enabled: !!familyId,
  });

  // Combine and filter content - same logic as original SelectionGrid
  const filteredContent = useMemo(() => {
    let content: Array<{type: 'recipe' | 'composition', data: RecipeWithStats | MealCompositionWithRecipes, createdAt: string}> = [];

    // Add recipes
    recipes.forEach(recipe => {
      content.push({
        type: 'recipe',
        data: recipe,
        createdAt: recipe.createdAt
      });
    });

    // Add compositions (unless excluded)
    if (!excludeCompositions) {
      compositions.forEach(composition => {
        content.push({
          type: 'composition',
          data: composition,
          createdAt: composition.createdAt
        });
      });
    }

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      content = content.filter(item => {
        if (item.type === 'recipe') {
          const recipe = item.data as RecipeWithStats;
          return recipe.title.toLowerCase().includes(query) ||
                 recipe.ingredients.some(ingredient => ingredient.toLowerCase().includes(query)) ||
                 recipe.tags?.some(tag => tag.toLowerCase().includes(query));
        } else {
          const composition = item.data as MealCompositionWithRecipes;
          return composition.name.toLowerCase().includes(query) ||
                 composition.recipes.some(recipe => recipe.title.toLowerCase().includes(query));
        }
      });
    }

    // Apply group filter (only to recipes)
    if (groupBy !== 'all') {
      content = content.filter(item => {
        if (item.type === 'composition') return true;
        
        const recipe = item.data as RecipeWithStats;
        const now = new Date();
        const createdDate = new Date(item.createdAt);
        const daysSinceCreated = Math.floor((now.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24));
        
        switch (groupBy) {
          case "never-used":
            return recipe.usageCount === 0;
          case "recently-used":
            const isRecentlyUsed = recipe.usageCount > 0 && (recipe.daysSinceLastUse === null || recipe.daysSinceLastUse <= 15);
            const isRecentlyCreated = daysSinceCreated <= 7;
            return isRecentlyUsed || isRecentlyCreated;
          default:
            return true;
        }
      });
    }

    // Apply sorting
    switch (sortBy) {
      case "alphabetical-asc":
        content.sort((a, b) => {
          const aTitle = a.type === 'recipe' ? (a.data as RecipeWithStats).title : (a.data as MealCompositionWithRecipes).name;
          const bTitle = b.type === 'recipe' ? (b.data as RecipeWithStats).title : (b.data as MealCompositionWithRecipes).name;
          return aTitle.localeCompare(bTitle);
        });
        break;
      case "alphabetical-desc":
        content.sort((a, b) => {
          const aTitle = a.type === 'recipe' ? (a.data as RecipeWithStats).title : (a.data as MealCompositionWithRecipes).name;
          const bTitle = b.type === 'recipe' ? (b.data as RecipeWithStats).title : (b.data as MealCompositionWithRecipes).name;
          return bTitle.localeCompare(aTitle);
        });
        break;
      case "date-asc":
        content.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
        break;
      case "date-desc":
        content.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        break;
      case "usage-desc":
        content.sort((a, b) => {
          if (a.type !== "recipe" || b.type !== "recipe") return 0;
          const aUsage = (a.data as RecipeWithStats)?.usageCount || 0;
          const bUsage = (b.data as RecipeWithStats)?.usageCount || 0;
          return bUsage - aUsage;
        });
        break;
      case "usage-asc":
        content.sort((a, b) => {
          if (a.type !== "recipe" || b.type !== "recipe") return 0;
          const aUsage = (a.data as RecipeWithStats)?.usageCount || 0;
          const bUsage = (b.data as RecipeWithStats)?.usageCount || 0;
          return aUsage - bUsage;
        });
        break;
    }

    return content;
  }, [recipes, compositions, searchQuery, sortBy, groupBy, excludeCompositions]);

  const handleClearFilters = () => {
    setSearchQuery('');
    setSortBy('date-desc');
    setGroupBy('all');
    setExcludeCompositions(false);
  };

  const isLoading = recipesLoading || compositionsLoading;

  if (isLoading) {
    return (
      <div className="text-center text-muted-foreground py-12">
        Loading recipes and compositions...
      </div>
    );
  }

  // Always use responsive CSS classes instead of JavaScript detection
  return (
    <div className="space-y-6">
      {/* Filters */}
      <RecipeFilters
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        sortBy={sortBy}
        onSortChange={setSortBy}
        filterBy="all"
        onFilterChange={() => {}}
        groupBy={groupBy}
        onGroupChange={setGroupBy}
        excludeCompositions={excludeCompositions}
        onExcludeCompositionsChange={setExcludeCompositions}
        onClearFilters={handleClearFilters}
        hideMobileFilters={true}
      />

      {/* Results Grid */}
      {filteredContent.length === 0 ? (
        <div className="text-center text-muted-foreground py-12">
          {searchQuery ? 'No recipes or compositions found matching your search.' : 'No recipes or compositions available.'}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2 md:grid-cols-1 md:sm:grid-cols-2 md:lg:grid-cols-3 md:gap-4">
          {filteredContent.map((item) => (
            <div key={`${item.type}-${item.data.id}`}>
              {item.type === 'recipe' ? (
                <>
                  {/* Mobile: Compact card */}
                  <div className="block md:hidden">
                    <CompactRecipeImageSelectionCard
                      recipe={item.data as RecipeWithStats}
                      onSelect={() => onSelect({ type: 'recipe', data: item.data as Recipe })}
                      isSubmitting={isSubmitting}
                    />
                  </div>
                  {/* Desktop: Full card */}
                  <div className="hidden md:block">
                    <RecipeImageSelectionCard
                      recipe={item.data as RecipeWithStats}
                      onSelect={() => onSelect({ type: 'recipe', data: item.data as Recipe })}
                      isSubmitting={isSubmitting}
                    />
                  </div>
                </>
              ) : (
                <CompositionImageSelectionCard
                  composition={item.data as MealCompositionWithRecipes}
                  onSelect={() => onSelect({ type: 'composition', data: item.data as MealCompositionWithRecipes })}
                  isSubmitting={isSubmitting}
                  isMobile={false} // CSS handles responsiveness
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ImageSelectionGrid;